import React from 'react';
import PageHeader from 'components/general/pageHeader';
import { useSelector } from 'react-redux';
import { apiGet } from 'services';
import { translateCompetency } from 'helpers/translations';
import { Copy, Help, Loading, SystemText } from 'e4shared';

import Bullet from 'components/prism/bullet';
import Course from 'components/prism/course';
import Rating from 'components/prism/rating';
import Resource from 'components/prism/resource';
import ObjectiveSummary from 'components/prism/objectiveSummary';
import { Link } from 'react-router-dom';
import BlobImage from 'components/blobImage';

export const Behaviour = ({ match }) => {
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState(null);

    const { user } = useSelector(state => state.user);
    const { prismMultiColumn } = useSelector(state => state.ui);

    React.useEffect(() => {
        if (user?.id && match.params.id) {
            apiGet(`behaviour-details/${match.params.id}/${user.id}`, user.guid, response => {
                setData(response);
                setLoading(false);
            });
        }
    }, [user?.id, match.params.id]);

    if (loading) return <Loading />;

    const text = {
        chart: {
            title: SystemText('BehaviourDetails/Chart/Title'), //Behaviour rating over time
            rating: SystemText('BehaviourDetails/Chart/Rating'), //Behaviour rating
            related: SystemText('BehaviourDetails/Chart/Related'), //Related objectives
        },
        table: {
            title: SystemText('BehaviourDetails/Table/Title'), //How we measure this Behaviour
            positive: SystemText('BehaviourDetails/Table/Positive'), //Positive indicator
            your: SystemText('BehaviourDetails/Table/Your'), //Your Rating
            manager: SystemText('BehaviourDetails/Table/Manager'), //Manager Rating
        },
        relatedTo: SystemText('BehaviourDetails/RelatedTo'), //Related to
        courses: SystemText('BehaviourDetails/Courses'), //Courses
        additional: SystemText('BehaviourDetails/Additional'), //Additional Resources
        completed: SystemText('BehaviourDetails/Completed'), //Completed
        resume: SystemText('Welcome/Pathway/Resume'), //Resume
        start: SystemText('Welcome/Pathway/Start'), //Start
    };

    return (
        <div id="behaviour-page" data-testid="behaviour-page">
            <PageHeader metaTitle={data.behaviour.name} />
            <div className="prism-top-banner mb30 mtm10">
                {data.behaviour.image ? (
                    <BlobImage src={data.behaviour.image} folder="competency_Image" noholder />
                ) : (
                    <img src="/dist/img/defaults/behavior-default.jpg" />
                )}
            </div>
            <div className={prismMultiColumn ? 'two-col-66' : ''}>
                <div className="">
                    <div className="flex flex-center mb20">
                        <div className="flex-grow">
                            <Bullet type="behavior" />
                        </div>
                        <div className="btn-fake">
                            <i className={`fa fa-flag ${data.behaviour.flagged ? 'fa-solid' : ''}`} />
                        </div>
                    </div>
                    <h1 className="mb10">{data.behaviour.name}</h1>
                    {data.behaviour.description && <p className="mb10">{data.behaviour.description}</p>}
                    <h3 className="mb10">{text.table.title}</h3>
                    <table className="prism-data-table mb20">
                        <thead>
                            <tr>
                                <th>{text.table.positive}</th>
                                <th className="text-center">
                                    <Copy value={text.table.your} />
                                </th>
                                <th className="text-center">
                                    <Copy value={text.table.manager} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    Presents information in a structured, logical way that is tailored to customer needs and
                                    uses the customer's language.
                                </td>
                                <td className="text-center">
                                    <Rating level={1} hideOutOf />
                                </td>
                                <td className="text-center">
                                    <Rating level={4} hideOutOf />
                                </td>
                            </tr>
                            <tr>
                                <td>Checks for customer feedback at regular and appropriate points.</td>
                                <td className="text-center">
                                    <Rating level={3} hideOutOf />
                                </td>
                                <td className="text-center">
                                    <Rating level={2} hideOutOf />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <h3 className="mb10">{text.relatedTo}</h3>
                    <div className="bullet-list">
                        <Bullet value="Metric name" style="plum" />
                        <Bullet value="Metric name" style="plum" />
                        <Bullet value="Metric name" style="plum" />
                        <Bullet value="Metric name" style="plum" />
                    </div>
                </div>
                <div className={`prism-content-box faded ${!prismMultiColumn ? 'mt20' : ''}`}>
                    <div className="prism-content">
                        <h6>{text.chart.title}</h6>
                        <div>
                            <svg
                                width="100%"
                                height="250"
                                style={{ border: '1px solid #ccc' }}
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <text y="50%" x="25%" lengthAdjust="spacingAndGlyphs">
                                    Chart here
                                </text>
                            </svg>
                        </div>
                        <h6 className="mt20">
                            <span>{text.chart.rating}</span>
                            <Help id="behaviour-rating">
                                This is the help text for the behaviour rating. It should provide more information about how
                                the rating is calculated and what it means.
                            </Help>
                        </h6>
                        <div>
                            <Rating level={data.behaviour.level} />
                        </div>
                        <h6 className="mt20">{text.chart.related}</h6>
                        <div className="bullet-list">
                            <Bullet value="Objective name here" style="info" />
                            <Bullet value="Objective name here" style="info" />
                        </div>
                    </div>
                </div>
            </div>
            {data.courses.length > 0 && (
                <div className="mt30">
                    <div className="flex flex-center mb10">
                        <h2>
                            {text.courses}
                            <span className="ml10" style={{ fontWeight: 400 }}>
                                {data.courses.length}
                            </span>
                        </h2>
                    </div>
                    {data.courses.length === 1 ? (
                        <Link data-testid="pathway-item" to={data.courses[0].url} className="prism-content-box">
                            {prismMultiColumn && (
                                <div className="prism-banner">
                                    <img src="/dist/img/defaults/course-default.jpg" />
                                </div>
                            )}
                            <div className="prism-content">
                                <Bullet type="course" />
                                <div className="prism-progress">
                                    <div className="prism-progress-bar">
                                        <div style={{ width: `${data.courses[0].progress}%` }} />
                                    </div>
                                    <div>{data.courses[0].progress}%</div>
                                </div>
                                <h3>{data.courses[0].name}</h3>
                                {data.courses[0].description && <p className="truncate-4">{data.courses[0].description}</p>}
                                <button className="btn btn-primary">
                                    {data.courses[0].progress ? text.resume : text.start}
                                </button>
                            </div>
                        </Link>
                    ) : (
                        <div className={prismMultiColumn ? 'even-col' : ''}>
                            {data.courses.slice(0, 3).map(course => (
                                <Course key={`course-${course.id}`} course={course} padBottom={!prismMultiColumn} />
                            ))}
                        </div>
                    )}
                </div>
            )}
            {data.resources.length > 0 && (
                <div className="mt30">
                    <div className="flex flex-center mb10">
                        <h2>
                            {text.additional}
                            <span className="ml10" style={{ fontWeight: 400 }}>
                                {data.resources.length}
                            </span>
                        </h2>
                    </div>
                    <div className={prismMultiColumn ? 'even-col' : ''}>
                        {data.resources.slice(0, 3).map(resource => (
                            <Resource key={`resource-${resource.id}`} resource={resource} padBottom={!prismMultiColumn} />
                        ))}
                    </div>
                </div>
            )}
            {data.completeds.length > 0 && (
                <div className="mt30">
                    <div className="flex flex-center mb10">
                        <h2>
                            {text.completed}
                            <span className="ml10" style={{ fontWeight: 400 }}>
                                {data.completeds.length}
                            </span>
                        </h2>
                    </div>
                    <div className={prismMultiColumn ? 'even-col' : ''}>
                        {data.completeds.slice(0, 3).map(objective => (
                            <ObjectiveSummary
                                key={`objective-${objective.id}`}
                                objective={objective}
                                padBottom={!prismMultiColumn}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Behaviour;
